import { mapGetters, mapActions, mapState } from 'vuex';
import draggable from 'vuedraggable';
import { cloneDeep } from 'lodash';

import commonMixins from '@/mixins/templates/common';
import contentMixin from '@/mixins/templates/content';
import ColorAndIconMixin from '@/mixins/content-icon-color.js';
import getIsTouchDeviceMixins from '@/mixins/check-is-touch-device';

import TeachersList from '@/components/content-v2/modals/teachers-list';
import TopicNavigation from '@/components/content-v2/topic-navigation';
import PageHeaderRender from '@/components/content-v2/templates/common/page-header-render';
import SelectContentType from '@/components/content-v2/select-content-type';
import TopicSettingsMenu from '@/components/content-v2/templates/common/topic-settings-menu';
import EmptyAssociatedWarning from '@/components/ui/empty-associated-warn';
import StatusChip from '@/components/content-v2/templates/common/status-chip';

import ContentType from '@/helpers/contentType';
import TemplateType from '@/helpers/templateType';
import CategoryType from '@/helpers/contentCategory';
import AcademicMainType from '@/helpers/academicMainType';
import { PROCESSING_STATUS } from '@/helpers/avaliaActivity';
import { SCORM_UPLOAD_STATUS } from '@/helpers/scorm';
import ConditionalTypes from '@/helpers/availabilityConditionalTypes';
import ContentSyncMixin from '@/mixins/content-sync';

export default {
  mixins: [ColorAndIconMixin, commonMixins, contentMixin, getIsTouchDeviceMixins, ContentSyncMixin],
  data() {
    return {
      SCORM_UPLOAD_STATUS,
      AcademicMainType,
      CategoryType,
      TemplateType,
      isOpenNavigationDrawer   : !(this.$vuetify.breakpoint.smAndDown || this.contentIsUnavailable),
      topicTitle               : '',
      ContentType,
      editingAvailabilityTopic : null
    };
  },
  components: {
    draggable,
    TeachersList,
    TopicNavigation,
    PageHeaderRender,
    TopicSettingsMenu,
    SelectContentType,
    EmptyAssociatedWarning,
    StatusChip
  },
  mounted() {
    this.$nuxt.$on('changeModalConditionalAvailability', event => {
      if (event.eventSource !== 'timeline-render') {
        return;
      }

      this.editingAvailabilityTopic.hasConditional = event.conditionals?.length > 0;
      this.editingAvailabilityTopic.conditionals = event.conditionals;
      this.editingAvailabilityTopic.isVisibleIsControlledByConditional = event.isVisibleIsControlledByConditional;
      this.editingAvailabilityTopic.version = this.getterContent.context.version;

      this.$nuxt.$loading.start();
      this.actionPatchTopic({
        data           : this.editingAvailabilityTopic,
        academicMainId : this.getterContent.context.academicMainId
      })
        .then(() => {
          this.$toast.success(this.$t('conditionalAvailability.successSaveMsg'));
        })
        .catch((error) => {
          this.$toast.error(error.message);
        })
        .finally(() => {
          this.$nuxt.$loading.finish();
        });

      this.editingAvailabilityTopic = null;
    });
  },
  computed: {
    ...mapGetters({
      getterContent         : 'plataforma/newContent/getterContent',
      getterUserRoleContext : 'plataforma/user/getterUserContext',
      isImpersonated        : '_core/auth/isImpersonated'
    }),
    ...mapState('plataforma/user', {
      isStudent   : state => state.context.isStudent,
      isLtiAccess : (state) => state.plataforma.user.isLtiAccess
    })
  },
  methods: {
    ...mapActions({
      actionPatchTopic          : 'plataforma/newContent/actionPatchTopic',
      actionPatchIsVisibleTopic : 'plataforma/newContent/actionPatchIsVisibleTopic'
    }),
    openConditionalModal(topic) {
      this.$nuxt.$emit('changeIsOpenConditionalsListModal', {
        conditionals        : topic.conditionals,
        isAccessCodeEnabled : topic.isAccessCodeEnabled
      });
    },
    goBack() {
      this.$router.push(`/course/${this.$route.params.courseId}`);
    },
    toggleNavigationDrawer() {
      this.isOpenNavigationDrawer = !this.isOpenNavigationDrawer;
    },
    openModalTopicTypes() {
      const params = {
        academicMainId     : this.getterContent.context.academicMainId,
        academicMainTypeId : this.getterContent.context.academicMainTypeId,
        parentTopic        : { ...this.getterContent.context, id: this.getterContent.context.topicId },
        version            : this.getterContent.context.version
      };

      this.$nuxt.$emit('changeIsOpenModalContentTypes', params);
    },
    academicMainTypeName() {
      return AcademicMainType.getName(this.getterContent.context && this.getterContent.context.academicMainTypeId);
    },
    isDeadlineLabelVisible(topic) {
      if (this.getterUserRoleContext.isStudent && topic.topicTypeId === ContentType.AVALIA && (!topic.content?.isActive || topic.content.status !== PROCESSING_STATUS.OK)) {
        return false;
      }

      if (this.getterContent.context.hasDeadline && this.getterContent.context.deadlineAt) {
        return topic.hasDeadline && this.$options.filters.formatDate(topic.deadlineAt) !== this.$options.filters.formatDate(this.getterContent.context.deadlineAt);
      }

      return topic.hasDeadline && topic.deadlineAt;
    },
    isAvaliaAndInactive(topic) {
      return topic.topicTypeId === ContentType.AVALIA && (!topic.content.isActive || topic.content.status !== PROCESSING_STATUS.OK);
    },
    isWebconferenceAndNotIntegrated(topic) {
      return topic.topicTypeId === ContentType.WEBCONFERENCE && !topic.content.isIntegrated;
    },
    getAlertMessage(topic) {
      if (topic.topicTypeId === ContentType.AVALIA) {
        return topic.content.status === PROCESSING_STATUS.OK ? this.$t('content.cardTopic.avaliaInactive') : this.$t('content.cardTopic.pendingUpdate');
      }
      if (topic.topicTypeId === ContentType.WEBCONFERENCE) {
        return this.$t('content.cardTopic.integrationError');
      }
    },
    isTopicDeadlineExpired(topic) {
      return this.getterUserRoleContext.isStudent
        && topic.hasOwnProperty('isBeforeDeadline')
        && !topic.isBeforeDeadline
        && topic.expired;
    },
    editPage() {
      const params = {
        topicId            : this.getterContent.context.topicId,
        academicMainTypeId : this.getterContent.context.academicMainTypeId,
        version            : this.getterContent.context.version,
        academicMainId     : this.getterContent.context.academicMainId,
        isEditing          : true
      };

      this.$nuxt.$emit('changeIsOpenModalContentPage', params);
    },
    showConditionalAvailability(topic) {
      this.$nuxt.$emit('changeIsOpenConditionalsListModal', {
        conditionals        : topic.conditionals,
        isAccessCodeEnabled : topic.isAccessCodeEnabled
      });
    },
    editConditionalAvailability(topic) {
      this.editingAvailabilityTopic = cloneDeep(topic);
      this.$nuxt.$emit('changeIsOpenConditionalAvailabilityModal', {
        isVisibleIsControlledByConditional : topic.isVisibleIsControlledByConditional,
        conditionals                       : topic.conditionals,
        eventSource                        : 'timeline-render'
      });
    },
    isFeatureLocked(feature) {
      return process.env.FEATURES_LOCK?.includes(feature);
    },
    hasConditionalError(topic) {
      const conditionalIsAcademicMainStart = topic.conditionals?.some(condition => condition.typeId === ConditionalTypes.DATE_ACADEMIC_MAIN);
      const academicMainStartAt = !!this.getterContent.context.academicMainStartAt;

      return !academicMainStartAt && conditionalIsAcademicMainStart;
    }
  },
  beforeDestroy() {
    this.$nuxt.$off('changeModalConditionalAvailability');
  }
};
